import React from 'react';
import {
  Users, KeyRound, Save, RefreshCw, Bug, Network,
  MessageCircle, CheckCircle2, Lightbulb, BarChart3, ArrowRight, Info, ShieldCheck,
} from 'lucide-react';
import { Card, Button, ProgressBar } from './ds';
import '../styles/check-intro.css';

// Intro-Seite des Cyber Risiko Checks (AUD-62).
//
// Alle Zahlen (Anforderungen, Themenbereiche, Maximalpunkte, Fragen je Bereich,
// geschätzte Dauer) stammen aus GET /api/public/audits/catalog — abgeleitet aus dem
// Fragenkatalog, den der Nutzer gleich beantwortet. Hier stehen nur Icons und
// Kurzbeschreibungen, die es im Katalog nicht gibt.
const ICON_STROKE = 1.75;

// Zuordnung Themenbereich → Icon + Kurzbeschreibung. Schlüssel sind die offiziellen
// Bereichsnamen der DIN SPEC 27076 (6.3.2), die der Katalog-Endpunkt liefert; ihre
// Schreibweise ist in backend/test/dinSpecCatalog.test.js gegen die Norm abgesichert.
const TOPIC_META = {
  'Organisation & Sensibilisierung': {
    icon: Users,
    desc: 'Verantwortlichkeiten, Notfallkontakt, Schulungen, Vertraulichkeitsregeln und Homeoffice-Richtlinien',
  },
  'Identitäts- und Berechtigungsmanagement': {
    icon: KeyRound,
    desc: 'Zutrittskontrolle, Zugriffsrechte, Passwortrichtlinien und 2-Faktor-Authentifizierung',
  },
  'Datensicherung': {
    icon: Save,
    desc: 'Backup-Intervalle, Schutz der Sicherungen, externes Speichern und regelmäßige Tests',
  },
  'Patch- und Änderungsmanagement': {
    icon: RefreshCw,
    desc: 'Software-Updates, zeitnahe Installation, Zuständigkeiten und veraltete Systeme',
  },
  'Schutz vor Schadprogrammen': {
    icon: Bug,
    desc: 'Virenschutz, vertrauenswürdige Softwarequellen, Installationsrechte und Makro-Schutz',
  },
  'IT-Systeme und Netzwerke': {
    icon: Network,
    desc: 'Firewall, Geräteschutz, VPN-Zugriff, WLAN-Sicherheit, Fernwartung und Elementarschutz',
  },
};

const STEPS = [
  { icon: MessageCircle, title: 'Frage lesen', desc: 'Zu jeder Anforderung wird Ihnen eine Frage gestellt.' },
  { icon: CheckCircle2, title: 'Ja oder Nein antworten', desc: 'Beantworten Sie ehrlich, ob die Anforderung in Ihrem Unternehmen erfüllt ist.' },
  { icon: Lightbulb, title: 'Handlungsempfehlung', desc: 'Bei „Nein" erscheint automatisch eine konkrete Handlungsempfehlung.' },
  { icon: BarChart3, title: 'Risiko-Status', desc: 'Am Ende erhalten Sie einen Punktwert als Ihren persönlichen Risiko-Status. Nicht zutreffende Bereiche entfallen, die erreichbare Punktzahl passt sich an.' },
];

const SECTION_TITLE = {
  margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-h3)',
  fontWeight: 'var(--fw-semibold)', color: 'var(--text-strong)', letterSpacing: 'var(--ls-heading)',
};

const StartButton = ({ onStart, size = 'lg' }) => (
  <Button size={size} onClick={onStart} trailingIcon={<ArrowRight width={16} height={16} strokeWidth={ICON_STROKE} />}>
    Cyber Risiko Check starten
  </Button>
);

// Eigene Kennzahl-Kachel für die dunkle Hero-Fläche (die ds-`Metric` ist auf
// helle Card-Flächen ausgelegt).
const HeroMetric = ({ value, label }) => (
  <div style={{ background: 'var(--ink-950)', padding: '18px 20px' }}>
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-h2)', fontWeight: 'var(--fw-semibold)',
      color: 'var(--white)', lineHeight: 'var(--lh-tight)',
    }}>{value}</div>
    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-300)', marginTop: 6 }}>{label}</div>
  </div>
);

const DinSpecIntro = ({ onStart, catalog }) => {
  const topics = catalog?.categories || [];
  const requirements = catalog?.requirements;
  const minutes = catalog?.estimatedMinutes;

  return (
    <div className="ci-page" style={{ fontFamily: 'var(--font-sans)' }}>

      {/* ── Hero ── */}
      <section className="ci-hero">
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(var(--ink-800) 1px, transparent 1px)',
          backgroundSize: '22px 22px', opacity: 0.6,
        }} />

        <div className="ci-hero-grid" style={{ position: 'relative' }}>
          <div>
            <div style={{
              fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-semibold)',
              letterSpacing: 'var(--ls-caps)', textTransform: 'uppercase',
              color: 'var(--ink-300)', marginBottom: 12,
            }}>
              IT-Sicherheit für kleine und Kleinstunternehmen
            </div>
            <h1 style={{
              margin: '0 0 14px', fontSize: 'var(--text-h1)', fontWeight: 'var(--fw-bold)',
              letterSpacing: 'var(--ls-display)', lineHeight: 'var(--lh-tight)', color: 'var(--white)',
            }}>
              Cyber Risiko Check
            </h1>
            <p style={{
              margin: '0 0 28px', fontSize: 'var(--text-base)', lineHeight: 'var(--lh-normal)',
              color: 'var(--ink-200)', maxWidth: '52ch',
            }}>
              Der Check erhebt den Ist-Zustand Ihrer IT-Sicherheit auf Basis der DIN SPEC 27076 —
              dem Mindeststandard für kleine und Kleinstunternehmen. Sie beantworten Fragen zu
              Ihrem Unternehmen und erhalten daraus Ihren Risiko-Status mit konkreten
              Handlungsempfehlungen.
            </p>

            <StartButton onStart={onStart} />
            <div style={{ marginTop: 12, fontSize: 'var(--text-sm)', color: 'var(--ink-300)' }}>
              Ihre Antworten werden automatisch gespeichert.
            </div>
          </div>

          {catalog && (
            <div className="ci-metrics">
              <HeroMetric value={catalog.requirements} label="Anforderungen" />
              <HeroMetric value={catalog.categoryCount} label="Themenbereiche" />
              <HeroMetric value={catalog.maxPoints} label="max. Punkte" />
              <HeroMetric value={minutes ? `~${minutes}` : '—'} label="Minuten Dauer" />
            </div>
          )}
        </div>
      </section>

      {/* ── So läuft der Check ab ── */}
      <Card padding={0}>
        <div style={{ padding: '22px 24px 18px' }}>
          <h2 style={SECTION_TITLE}>So läuft der Check ab</h2>
        </div>

        <div className="ci-steps" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {STEPS.map((step, i) => (
            <div key={step.title} className="ci-step" style={{ padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, color: 'var(--brand-600)' }}>
                <step.icon width={18} height={18} strokeWidth={ICON_STROKE} />
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                  color: 'var(--text-faint)',
                }}>{i + 1}</span>
              </div>
              <div style={{
                fontSize: 'var(--text-sm)', fontWeight: 'var(--fw-semibold)',
                color: 'var(--text-strong)', marginBottom: 4,
              }}>{step.title}</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--lh-normal)' }}>
                {step.desc}
              </div>
            </div>
          ))}
        </div>

        {requirements > 0 && (
          <div style={{
            borderTop: '1px solid var(--border-subtle)', padding: '16px 24px',
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              Ihr Fortschritt
            </span>
            <ProgressBar
              value={0}
              max={requirements}
              size="sm"
              style={{ flex: 1 }}
              role="progressbar"
              aria-label="Beantwortete Anforderungen"
              aria-valuemin={0}
              aria-valuemax={requirements}
              aria-valuenow={0}
            />
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-mono)',
              color: 'var(--text-body)', whiteSpace: 'nowrap',
            }}>0 / {requirements} Anforderungen</span>
          </div>
        )}
      </Card>

      {/* ── Themenbereiche ── */}
      {topics.length > 0 && (
        <Card padding={0}>
          <div style={{
            padding: '22px 24px 18px', display: 'flex', alignItems: 'baseline',
            justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <h2 style={SECTION_TITLE}>Die {catalog.categoryCount} Themenbereiche</h2>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{catalog.totalQuestions}</span> Fragen insgesamt
            </span>
          </div>

          <div className="ci-topics" style={{ borderRadius: 0, border: 0, borderTop: '1px solid var(--border-subtle)' }}>
            {topics.map((topic) => {
              const meta = TOPIC_META[topic.title];
              const TopicIcon = meta?.icon || ShieldCheck;
              return (
                <div key={topic.title} className="ci-topic" style={{ padding: '20px 24px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                    color: 'var(--brand-600)',
                  }}>
                    <TopicIcon width={19} height={19} strokeWidth={ICON_STROKE} />
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-faint)',
                    }}>{topic.questions} Fragen</span>
                  </div>
                  <div style={{
                    fontSize: 'var(--text-sm)', fontWeight: 'var(--fw-semibold)',
                    color: 'var(--text-strong)', marginBottom: 4,
                  }}>{topic.title}</div>
                  {meta?.desc && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--lh-normal)' }}>
                      {meta.desc}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Vertraulichkeit & Ehrlichkeit + CTA ── */}
      <Card padding={24}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 32, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: '1 1 420px' }}>
            <span style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }}>
              <Info width={20} height={20} strokeWidth={ICON_STROKE} />
            </span>
            <div>
              <div style={{
                fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)',
                color: 'var(--text-strong)', marginBottom: 6,
              }}>
                Vertraulichkeit und Ehrlichkeit
              </div>
              <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--lh-normal)' }}>
                Die Erhebung dient ausschließlich dazu, den Ist-Zustand Ihrer IT-Sicherheit
                festzustellen. Bitte beantworten Sie alle Fragen nach bestem Wissen und Gewissen —
                es gibt keine richtigen oder falschen Antworten, nur ehrliche. Auch die volle
                Punktzahl bedeutet lediglich, dass das Minimum an Informationssicherheit für
                kleine Unternehmen nachgewiesen ist.
              </p>
            </div>
          </div>

          <div style={{ flexShrink: 0 }}>
            <StartButton onStart={onStart} size="md" />
            {minutes && (
              <div style={{ marginTop: 10, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                ~{minutes} min · jederzeit unterbrechbar
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DinSpecIntro;
